import React, { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import {
    ArrowLeftOutlined,
    AudioOutlined,
    CheckCircleFilled,
    CheckOutlined,
    FilePdfOutlined,
    LoadingOutlined,
    PaperClipOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { Button, Card } from "../../../design-system";
import { ReactComponent as DocumentIcon } from "../../../assets/icons/svg/file-gradient.svg";
import {
    BOT_AFTER_MSA_READY,
    BOT_AFTER_ORDER_FORM_READY,
    CONTEXT_FROM_ACTION,
    REF_ID,
    TRANSCRIPTS,
} from "./demoScript";
import { useIntakeMachine } from "./useIntakeMachine";
import type {
    ChatMessage,
    ChatMessagePart,
    ContextItem,
    DealHeader,
    DocItem,
} from "./types";
import classes from "./DemoDealIntake.module.less";

const HeaderTitle: React.FC<{ header: DealHeader }> = ({ header }) => {
    // null = user hasn't typed yet → fall through to the auto-detected name (or
    // placeholder). Once the user edits, their value sticks even if the bot
    // later auto-populates a name.
    const [localName, setLocalName] = React.useState<string | null>(null);
    const value = localName ?? header.dealName ?? "";
    return (
        <input
            className={classes.headerTitle}
            type="text"
            value={value}
            placeholder="New Project"
            onChange={(e) => setLocalName(e.target.value)}
            spellCheck={false}
        />
    );
};

// Brand mark from infinity-loop-logo-horz-color-wt.svg, cropped to just the
// infinity-curl icon so it can sit inside the chat avatar.
const BrandMark: React.FC = () => (
    <svg viewBox="0 0 110 61" fill="none" aria-label="Infinity Loop">
        <path
            d="M108.9083736,24.6111203L87.5611864,3.26393c-2.9444372-2.94444-7.7291564-2.94444-10.6735936,0l-36.6214753,36.4374449c-2.7604125,2.5763843-7.3611,5.1527686-11.0416518,5.1527686-4.0486047,0-8.0972093-1.840275-10.8576218-4.7847157-2.7604125-3.1284689-4.0486047-7.1770718-3.68055-11.4097047.7361111-6.8090172,6.2569343-12.145814,13.0659532-12.6978986h1.4722204c3.68055,0,7.1770753,1.2881922,9.7534596,3.8645782l6.4409625,6.4409625c.5520811.5520829,1.6562468.5520829,2.2083314,0l7.3611-7.3611c.5520811-.5520829.5520811-1.6562468,0-2.2083297l-4.4166593-4.4166602-2.0243067-2.0243006-.1840282-.1840273c-5.8888814-5.520825-13.8020625-8.2812375-21.7152471-7.5451273C13.3980991,3.8160124,2.908532,14.1215523,1.4363119,27.1875046c-.9201375,7.9131829,1.6562473,15.826364,6.993045,21.7152454,5.3367977,5.8888814,12.8819241,9.3854032,20.795107,9.3854032,10.121516,0,18.2187218-6.8090189,20.4270532-8.8333221l31.6527314-31.284675c.5520811-.5520829,1.6562503-.5520829,2.2083314,0l11.2256747,11.2256782c.5520811.5520829.5520811,1.6562468,0,2.2083314l-11.2256747,11.2256782c-.5520811.5520811-1.6562503.5520811-2.2083314,0l-8.4652622-8.4652657c-.5520811-.5520811-1.6562503-.5520811-2.2083314,0l-7.3611,7.3611c-.5520811.5520811-.5520811,1.6562468,0,2.2083314l13.6180378,13.6180343c1.4722186,1.6562468,3.4965183,2.3923561,5.3367933,2.3923561s3.8645747-.7361093,5.3368003-2.2083314l21.3471872-21.3471907c4.0486064-3.8645747,3.68055-8.0972076,0-11.7777576h0Z"
            fill="#24b2ee"
        />
    </svg>
);

const OrderFormSummaryBubble: React.FC = () => (
    <div>
        <p>{BOT_AFTER_ORDER_FORM_READY.intro}</p>
        <p className={classes.bubbleParagraph}>{BOT_AFTER_ORDER_FORM_READY.body}</p>
        <p className={classes.bubbleParagraph}>{BOT_AFTER_ORDER_FORM_READY.followUp}</p>
    </div>
);

const MSASummaryBubble: React.FC = () => (
    <div>
        <p>{BOT_AFTER_MSA_READY.intro}</p>
        <p className={classes.bubbleParagraph}>{BOT_AFTER_MSA_READY.body}</p>
        <p className={classes.bubbleParagraph}>{BOT_AFTER_MSA_READY.followUp}</p>
    </div>
);

const renderPart = (part: ChatMessagePart, idx: number): React.ReactNode => {
    switch (part.type) {
        case "html":
            return (
                <div key={idx} className={classes.bubbleContent}>
                    {part.content}
                </div>
            );
        case "longtext":
            return (
                <div key={idx} className={clsx(classes.bubbleContent, classes.bubbleLong)}>
                    {part.content}
                </div>
            );
        case "doc":
            return (
                <div key={idx} className={classes.docCard}>
                    <FilePdfOutlined className={classes.docCardIcon} />
                    <div className={classes.docCardInfo}>
                        <div className={classes.docCardName}>{part.filename}</div>
                        <div className={classes.docCardMeta}>{part.size} · just now</div>
                    </div>
                </div>
            );
        case "orderFormSummary":
            return (
                <div key={idx} className={classes.bubbleContent}>
                    <OrderFormSummaryBubble />
                </div>
            );
        case "msaSummary":
            return (
                <div key={idx} className={classes.bubbleContent}>
                    <MSASummaryBubble />
                </div>
            );
    }
};

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
    if (message.typing) {
        return (
            <div className={clsx(classes.message, classes.messageFromBot)}>
                <div className={clsx(classes.messageAvatar, classes.messageAvatarBrand)}>
                    <BrandMark />
                </div>
                <div className={classes.messageBody}>
                    <div className={classes.typing}>
                        <span />
                        <span />
                        <span />
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div
            className={clsx(
                classes.message,
                message.role === "bot" ? classes.messageFromBot : classes.messageFromUser,
                message.interjection && classes.messageInterjection
            )}
        >
            <div
                className={clsx(
                    classes.messageAvatar,
                    message.role === "bot" && classes.messageAvatarBrand
                )}
            >
                {message.role === "bot" ? <BrandMark /> : "D"}
            </div>
            <div className={classes.messageBody}>
                {message.interjection && (
                    <div className={classes.interjectionTag}>Update from documents</div>
                )}
                {message.parts.map(renderPart)}
            </div>
        </div>
    );
};

const DocRow: React.FC<{ doc: DocItem }> = ({ doc }) => {
    const isSpinning = doc.state === "uploading" || doc.state === "parsing";
    const isReady = doc.state === "ready";
    const isSuggested = doc.state === "suggested";

    let statusText = doc.hint ?? "";
    if (doc.state === "uploading") {
        statusText = `Uploading… ${Math.round(doc.progress ?? 0)}%`;
    } else if (doc.state === "parsing") {
        statusText = "Reading content…";
    } else if (doc.state === "ready") {
        statusText = doc.size ?? "";
    }

    return (
        <div className={clsx(classes.docRow, isReady && classes.docRowReady)}>
            <div className={classes.docRowCheck}>
                {isSpinning ? (
                    <LoadingOutlined className={classes.docRowSpinner} />
                ) : isReady ? (
                    <CheckCircleFilled className={classes.docRowCheckReady} />
                ) : (
                    <div
                        className={clsx(
                            classes.docRowCheckbox,
                            isSuggested && classes.docRowCheckboxDashed
                        )}
                    />
                )}
            </div>
            <div className={classes.docRowBody}>
                <div className={classes.docRowLabelLine}>
                    <span className={classes.docRowLabel}>{doc.label}</span>
                    {isReady && doc.detectedType && (
                        <span className={classes.docRowTagDetected}>
                            {doc.detectedType.toUpperCase()}
                        </span>
                    )}
                </div>
                {statusText && <div className={classes.docRowStatus}>{statusText}</div>}
                {doc.state === "uploading" && (
                    <div className={classes.docRowProgress}>
                        <span style={{ width: `${doc.progress ?? 0}%` }} />
                    </div>
                )}
            </div>
        </div>
    );
};

const ContextRow: React.FC<{ item: ContextItem }> = ({ item }) => (
    <div className={clsx(classes.contextRow, item.status === "done" && classes.contextRowDone)}>
        <div className={classes.contextRowCheck}>
            {item.status === "done" ? <CheckOutlined /> : null}
        </div>
        <div className={classes.contextRowBody}>
            <div className={classes.contextRowLabel}>{item.label}</div>
            <div className={classes.contextRowHint}>{item.hint}</div>
        </div>
    </div>
);


const ReviewView: React.FC<{
    header: DealHeader;
    documents: DocItem[];
    context: ContextItem[];
    transcripts: Record<string, string>;
    onBack: () => void;
    onSubmit: (projectName: string) => void;
}> = ({ header, documents, context, transcripts, onBack, onSubmit }) => {
    const [projectName, setProjectName] = useState<string>(header.dealName ?? "");
    const [contextDrafts, setContextDrafts] = useState<Record<string, string>>(() => {
        const out: Record<string, string> = {};
        context.forEach((c) => {
            out[c.id] = transcripts[c.id] ?? "";
        });
        return out;
    });
    const [extraDocs, setExtraDocs] = useState<{ id: string; filename: string; size: string }[]>(
        []
    );
    const [extraContext, setExtraContext] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const readyDocs = documents.filter((d) => d.state === "ready");

    const EXTRA_DOC_SAMPLES = [
        { filename: "Q1-2026-spend-forecast.xlsx", size: "184 KB" },
        { filename: "Azure-AWS-migration-cost-model.pdf", size: "412 KB" },
        { filename: "Marketplace-rollout-plan.docx", size: "67 KB" },
    ];

    const attachSampleDoc = () => {
        const sample = EXTRA_DOC_SAMPLES[extraDocs.length % EXTRA_DOC_SAMPLES.length];
        setExtraDocs((prev) => [
            ...prev,
            { id: `extra-${prev.length}-${Date.now()}`, ...sample },
        ]);
    };

    return (
        <div className={classes.reviewPage}>
            <div className={classes.reviewBody}>
                <section className={classes.reviewField}>
                    <label htmlFor="review-project-name" className={classes.reviewFieldLabel}>
                        Project name
                    </label>
                    <input
                        id="review-project-name"
                        type="text"
                        className={classes.reviewTextInput}
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="New Project"
                        spellCheck={false}
                    />
                </section>

                <section className={classes.reviewField}>
                    <div className={classes.reviewFieldLabel}>Documents</div>
                    {(readyDocs.length > 0 || extraDocs.length > 0) && (
                        <ul className={classes.reviewDocList}>
                            {readyDocs.map((d) => (
                                <li key={d.id}>
                                    <FilePdfOutlined className={classes.reviewDocIcon} />
                                    <div className={classes.reviewDocInfo}>
                                        <div className={classes.reviewDocName}>{d.filename}</div>
                                    </div>
                                    {d.detectedType && (
                                        <span className={classes.reviewDocTag}>
                                            {d.detectedType.toUpperCase()}
                                        </span>
                                    )}
                                    {d.size && (
                                        <div className={classes.reviewDocBytes}>{d.size}</div>
                                    )}
                                </li>
                            ))}
                            {extraDocs.map((d) => (
                                <li key={d.id}>
                                    <FilePdfOutlined className={classes.reviewDocIcon} />
                                    <div className={classes.reviewDocInfo}>
                                        <div className={classes.reviewDocName}>{d.filename}</div>
                                    </div>
                                    <div className={classes.reviewDocBytes}>{d.size}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div
                        className={clsx(
                            classes.reviewDropZone,
                            dragOver && classes.reviewDropZoneActive
                        )}
                        onDragOver={(e) => {
                            e.preventDefault();
                            setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragOver(false);
                            attachSampleDoc();
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <DocumentIcon className={classes.reviewDropZoneIcon} />
                        <div className={classes.reviewDropZoneText}>
                            Drop or click to add files
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: "none" }}
                            onChange={(e) => {
                                e.preventDefault();
                                attachSampleDoc();
                                if (fileInputRef.current) fileInputRef.current.value = "";
                            }}
                        />
                    </div>
                </section>

                {context.length > 0 && (
                    <section className={classes.reviewField}>
                        <div className={classes.reviewFieldLabel}>Context</div>
                        <div className={classes.reviewContextGroup}>
                            {context.map((c) => (
                                <div key={c.id} className={classes.reviewContextItem}>
                                    <label
                                        htmlFor={`review-context-${c.id}`}
                                        className={classes.reviewContextItemLabel}
                                    >
                                        {c.label}
                                    </label>
                                    <textarea
                                        id={`review-context-${c.id}`}
                                        className={classes.reviewTextArea}
                                        rows={4}
                                        value={contextDrafts[c.id] ?? ""}
                                        onChange={(e) =>
                                            setContextDrafts((prev) => ({
                                                ...prev,
                                                [c.id]: e.target.value,
                                            }))
                                        }
                                        placeholder={c.hint}
                                    />
                                </div>
                            ))}
                            <div className={classes.reviewContextItem}>
                                <label
                                    htmlFor="review-context-extra"
                                    className={classes.reviewContextItemLabel}
                                >
                                    More context
                                </label>
                                <textarea
                                    id="review-context-extra"
                                    className={classes.reviewTextArea}
                                    rows={4}
                                    value={extraContext}
                                    onChange={(e) => setExtraContext(e.target.value)}
                                    placeholder="Anything else the analyst should know…"
                                />
                            </div>
                        </div>
                    </section>
                )}
            </div>

            <div className={classes.reviewFooter}>
                <Button size="large" icon={<ArrowLeftOutlined />} onClick={onBack}>
                    Back to intake
                </Button>
                <Button
                    type="primary"
                    size="large"
                    onClick={() => onSubmit(projectName.trim())}
                >
                    Submit for analysis
                </Button>
            </div>
        </div>
    );
};

const SubmittedView: React.FC<{ onReturn: () => void }> = ({ onReturn }) => (
    <div className={classes.submittedCard}>
        <CheckCircleFilled className={classes.submittedIcon} />
        <h2>Submitted for analysis</h2>
        <p>
            Your project has been queued. An analyst will pick it up and reach out within one
            business day with their findings.
        </p>
        <div className={classes.refTag}>
            Reference: <strong>{REF_ID}</strong>
        </div>
        <div className={classes.submittedActions}>
            <Button type="primary" onClick={onReturn}>
                Return to dashboard
            </Button>
        </div>
    </div>
);

export interface DemoDealIntakeViewProps {
    /** Called when the user clicks "Return to dashboard" after submitting. */
    onSubmittedReturn?: () => void;
    /** Fires when the view enters or exits review mode (lets the parent dialog
     * adjust its title). */
    onReviewModeChange?: (inReview: boolean) => void;
    /** Overrides the in-dialog "submitted" view — when provided, submitting in
     * review calls this with the final project name (parent navigates away). */
    onSubmit?: (projectName: string) => void;
}

export const DemoDealIntakeView: React.FC<DemoDealIntakeViewProps> = ({
    onSubmittedReturn,
    onReviewModeChange,
    onSubmit: onSubmitOverride,
}) => {
    const {
        state,
        runAction,
        submit,
        closeReview,
        firstActionByPrefix,
        reviewReady,
    } = useIntakeMachine();
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const el = messagesContainerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [state.messages, state.recordingTranscript]);

    // Keep the textarea visually pinned to the last line while the transcript
    // grows during a simulated recording.
    useEffect(() => {
        const el = textAreaRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [state.recordingTranscript]);

    // Let the wrapping dialog know whether we're in review mode so it can
    // swap its title.
    useEffect(() => {
        onReviewModeChange?.(state.reviewOpen);
    }, [state.reviewOpen, onReviewModeChange]);

    const placeholder = state.recordingTranscript !== null
        ? "Recording — speak naturally…"
        : "Type a message, or use the mic to speak…";

    const inputValue = state.recordingTranscript ?? "";

    // Transcripts the user "said" for each completed context topic. Looked up
    // by context id from the scripted TRANSCRIPTS table — keyed lookup avoids
    // the off-by-one that happens when other voice actions (like
    // talkProjectType) inject user longtexts that aren't tied to a context.
    const recordedTranscripts = React.useMemo(() => {
        const actionByContext: Record<string, keyof typeof TRANSCRIPTS> = {};
        (Object.entries(CONTEXT_FROM_ACTION) as [
            keyof typeof CONTEXT_FROM_ACTION,
            keyof typeof CONTEXT_FROM_ACTION[keyof typeof CONTEXT_FROM_ACTION] | string,
        ][]).forEach(([action, ctx]) => {
            actionByContext[ctx as string] = action as keyof typeof TRANSCRIPTS;
        });
        const out: Record<string, string> = {};
        state.contextItems
            .filter((c) => c.status === "done")
            .forEach((c) => {
                const action = actionByContext[c.id];
                if (action) out[c.id] = TRANSCRIPTS[action];
            });
        return out;
    }, [state.contextItems]);

    if (state.submitted) {
        return (
            <div className={classes.page}>
                <SubmittedView onReturn={onSubmittedReturn ?? closeReview} />
            </div>
        );
    }

    if (state.reviewOpen) {
        return (
            <div className={classes.page}>
                <ReviewView
                    header={state.header}
                    documents={state.documents}
                    context={state.contextItems}
                    transcripts={recordedTranscripts}
                    onBack={closeReview}
                    onSubmit={(projectName) => {
                        if (onSubmitOverride) onSubmitOverride(projectName);
                        else submit();
                    }}
                />
            </div>
        );
    }

    return (
        <div className={classes.page}>
            <div className={classes.headerRow}>
                <HeaderTitle header={state.header} />
                <div className={classes.checklistTitle}>Checklist</div>
            </div>
            <div className={classes.mainGrid}>
                <Card className={classes.chatPane} bordered>
                    <div className={classes.messages} ref={messagesContainerRef}>
                        {state.messages.map((m) => (
                            <MessageBubble key={m.id} message={m} />
                        ))}
                    </div>

                    <div className={classes.composer}>
                        <div
                            className={clsx(
                                classes.inputShell,
                                state.recordingTranscript !== null && classes.inputShellRecording
                            )}
                        >
                            <textarea
                                ref={textAreaRef}
                                className={classes.textArea}
                                placeholder={placeholder}
                                value={inputValue}
                                readOnly
                                rows={4}
                            />
                            <div className={classes.inputControls}>
                                <button
                                    type="button"
                                    className={classes.iconButton}
                                    title="Attach document"
                                    onClick={() => {
                                        const a = firstActionByPrefix("upload");
                                        if (a) runAction(a);
                                    }}
                                    disabled={!firstActionByPrefix("upload")}
                                >
                                    <PaperClipOutlined />
                                </button>
                                <button
                                    type="button"
                                    className={clsx(
                                        classes.iconButton,
                                        classes.micButton,
                                        state.recordingTranscript !== null && classes.micButtonListening
                                    )}
                                    title="Tap to record"
                                    onClick={() => {
                                        const a = firstActionByPrefix("talk");
                                        if (a) runAction(a);
                                    }}
                                    disabled={
                                        !firstActionByPrefix("talk") ||
                                        state.recordingTranscript !== null
                                    }
                                >
                                    <AudioOutlined />
                                </button>
                                {state.recordingTranscript !== null && (
                                    <div className={classes.listeningHint}>
                                        <span className={classes.waveform}>
                                            <span />
                                            <span />
                                            <span />
                                            <span />
                                            <span />
                                        </span>
                                        Recording…
                                    </div>
                                )}
                                <div className={classes.spacer} />
                                <button
                                    type="button"
                                    className={clsx(classes.iconButton, classes.sendButton)}
                                    title="Send"
                                    onClick={() => {
                                        const a = firstActionByPrefix("talk");
                                        if (a) runAction(a, "typed");
                                    }}
                                    disabled={
                                        !firstActionByPrefix("talk") ||
                                        state.recordingTranscript !== null
                                    }
                                >
                                    <SendOutlined />
                                </button>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className={classes.checklistPane} bordered>
                    <div className={classes.checklistSection}>
                        <div className={classes.sectionLabel}>Documents</div>
                        {state.documents.length === 0 ? (
                            <div className={classes.emptyHint}>
                                Suggested documents will appear here.
                            </div>
                        ) : (
                            state.documents.map((d) => <DocRow key={d.id} doc={d} />)
                        )}
                    </div>

                    <div className={classes.checklistSection}>
                        <div className={classes.sectionLabel}>Context</div>
                        {state.contextItems.length === 0 ? (
                            <div className={classes.emptyHint}>
                                Suggested topics will appear here.
                            </div>
                        ) : (
                            state.contextItems.map((c) => <ContextRow key={c.id} item={c} />)
                        )}
                    </div>

                    <div className={classes.reviewButtonWrap}>
                        <Button
                            type="primary"
                            size="large"
                            block
                            disabled={!reviewReady}
                            onClick={() => runAction("review")}
                        >
                            Review &amp; submit
                        </Button>
                    </div>
                </Card>
            </div>

        </div>
    );
};

export default DemoDealIntakeView;
